// 3.C.2 — "Käytä pohjana" -modaali (ADR-011:n kolmas restore-modi, fork).
// Käyttäjälle näkyvä termi on "Käytä pohjana" — "fork" on jätetty koodi-
// tasolle (API-polku /fork, DB-kenttä forked_from_project_id) koska
// tekninen termi ei avaudu loppukäyttäjälle. Avautuu project-detail-sivulta:
// kysyy uudelle projektille nimen (default: source.name + " (kopio)"),
// onnistuneen luonnin jälkeen onCreated-callback navigoi uudelle sivulle.

import { useEffect, useState } from "react";
import { apiClient, ApiError } from "../api/client.js";
import type { Project } from "../api/types.js";

export type ForkProjectDialogProps = {
  open: boolean;
  source: Project | null;
  onClose: () => void;
  /** Kutsutaan onnistuneen forkin jalkeen — kayttaja redirectaa /projektit/:id. */
  onCreated: (project: { id: string; name: string }) => void;
};

export function ForkProjectDialog({
  open,
  source,
  onClose,
  onCreated,
}: ForkProjectDialogProps) {
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setProcessing] = useState(false);

  useEffect(() => {
    if (open && source) {
      // Default-ehdotus: source-nimi + " (kopio)"
      setName(`${source.name} (kopio)`);
      setError(null);
      setProcessing(false);
    }
    if (!open) {
      setName("");
      setError(null);
      setProcessing(false);
    }
  }, [open, source]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !isProcessing) onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, isProcessing, onClose]);

  if (!open || !source) return null;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!source || !name.trim() || isProcessing) return;
    setError(null);
    setProcessing(true);
    try {
      const res = await apiClient.forkProject(source.id, name.trim());
      onCreated({ id: res.project.id, name: res.project.name });
      onClose();
    } catch (err) {
      if (err instanceof ApiError) setError(err.message);
      else setError(err instanceof Error ? err.message : String(err));
      setProcessing(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-slate-950/80 px-4"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget && !isProcessing) onClose();
      }}
    >
      <form
        onSubmit={submit}
        className="w-full max-w-md rounded-lg border border-slate-800 bg-slate-950 p-6 shadow-2xl"
      >
        <h3 className="text-lg font-semibold tracking-tight text-slate-100">
          Käytä pohjana
        </h3>
        <p className="mt-1 text-sm text-slate-400">
          Luodaan uusi projekti pohjautuen projektiin <strong>{source.name}</strong>.
          Uudella projektilla on oma id ja tyhjä tiedostohistoria — alkuperäinen
          projekti pysyy ennallaan.
        </p>
        <label className="mt-4 block">
          <span className="text-sm font-medium text-slate-300">Uuden projektin nimi</span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
            disabled={isProcessing}
            className="mt-1 block w-full rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-slate-100 focus:border-slate-500 focus:outline-none"
            placeholder={`${source.name} (kopio)`}
          />
        </label>
        {error && (
          <p className="mt-3 rounded-md border border-red-700 bg-red-950/40 px-3 py-2 text-sm text-red-200">
            {error}
          </p>
        )}
        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={isProcessing}
            className="rounded-md border border-slate-700 bg-slate-900 px-4 py-2 text-sm text-slate-200 hover:bg-slate-800 disabled:opacity-50"
          >
            Peruuta
          </button>
          <button
            type="submit"
            disabled={isProcessing || !name.trim()}
            className="rounded-md bg-slate-100 px-4 py-2 text-sm font-medium text-slate-900 hover:bg-white disabled:opacity-50"
          >
            {isProcessing ? "Luodaan…" : "Luo projekti"}
          </button>
        </div>
      </form>
    </div>
  );
}
